/**
 * Real-time list of the signed-in user's reminders.
 *
 * Subscribes to `users/{uid}/reminders` ordered by `dueAt` ascending. The route
 * groups the results into overdue / upcoming / done buckets.
 */
import type { ReminderDoc } from '@agente/shared'
import { onSnapshot, orderBy, query } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { useAuth } from '@/lib/auth'
import { remindersCol } from '@/lib/firestore'

export interface UseRemindersResult {
  reminders: ReminderDoc[]
  loading: boolean
  error: string | null
}

export function useReminders(): UseRemindersResult {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const [reminders, setReminders] = useState<ReminderDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uid) {
      setReminders([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)

    const q = query(remindersCol(uid), orderBy('dueAt', 'asc'))
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setReminders(snap.docs.map((d) => ({ ...d.data(), id: d.id })))
        setLoading(false)
      },
      (err) => {
        setError(err.message)
        setLoading(false)
      },
    )
    return unsubscribe
  }, [uid])

  return { reminders, loading, error }
}
